import {
  CONTAINER_EQUIPMENT,
  ItemKind,
  MAX_TRADE_ITEMS,
  TradeState,
  counted,
  fallbackSellPrice,
  itemType,
  specialtyHas,
  type BankPacket,
  type InventoryPacket,
  type ShopPacket,
  type ShopSpecialty,
  type TradePacket,
} from '@game/protocol';

import { attachItemTooltip, hideItemTooltip } from './item-tooltip.ts';
import { collapsiblePanel, type PanelId } from './panel.ts';

/**
 * As três janelas de economia: loja de NPC, banco e troca entre jogadores (PRD §5 e §6).
 *
 * Um arquivo para as três porque elas se excluem — não dá para estar num balcão, no banco e
 * numa troca ao mesmo tempo — e porque a regra que mais importa é a mesma nas três: **a
 * janela fecha sozinha quando o servidor diz que acabou**. Quem se afasta do mercador não
 * precisa lembrar de fechar nada, e uma janela que sobrevive ao fim da conversa é uma
 * janela que promete uma ação que vai ser recusada.
 *
 * O painel mora na barra lateral, **acima** do inventário, e não sobre o mundo. É a mesma
 * decisão do `index.html`: num jogo em que a criatura chega pela borda da tela, o canto
 * coberto por um painel é o canto de onde vem o perigo. E o inventário continua à vista de
 * propósito — é de lá que saem os itens arrastados para a mesa de troca.
 */
export interface CommerceHandlers {
  onBuy(typeId: number, count: number): void;
  onSell(typeId: number, count: number): void;
  /** Conserta **tudo** que está gasto. Sem item nem quantidade — ver `ShopAction.Repair`. */
  onRepair(): void;
  onCloseShop(): void;

  onDeposit(amount: number): void;
  onWithdraw(amount: number): void;
  onCloseBank(): void;

  onTradeAccept(): void;
  onTradeCancel(): void;
  onTradeWithdraw(index: number): void;
  onTradeOffer(container: number, slot: number): void;
}

export class CommercePanel {
  private readonly root: HTMLElement;
  private readonly handlers: CommerceHandlers;

  private shop: ShopPacket | null = null;
  private bank: BankPacket | null = null;
  private trade: TradePacket | null = null;

  /**
   * O último inventário que chegou, guardado por referência (PRD §15.7).
   *
   * O balcão precisa dele porque compra **fora da vitrine**: sem saber o que o jogador
   * carrega, a seção de fallback não teria o que listar, e o preço de fallback continuaria
   * invisível — que é o defeito inteiro. Referência, e não uma cópia filtrada: uma segunda
   * lista aqui envelheceria em silêncio na primeira venda, e a janela mostraria o peixe que
   * o jogador acabou de vender.
   */
  private inventory: InventoryPacket | null = null;

  constructor(root: HTMLElement, handlers: CommerceHandlers) {
    this.root = root;
    this.handlers = handlers;
    this.root.className = 'commerce';
    this.render();
  }

  /** Alguma das três janelas está aberta? A cena esconde o inventário quando sim. */
  get isOpen(): boolean {
    return this.shop !== null || this.bank !== null || this.trade !== null;
  }

  setShop(packet: ShopPacket): void {
    // Vitrine vazia é o "fechado" do protocolo: sem opcode próprio, e sem um estado a mais
    // para o servidor lembrar de limpar.
    this.shop = packet.entries.length > 0 ? packet : null;
    this.render();
  }

  /**
   * O inventário chegou. Só o balcão se importa — e só ele se redesenha (PRD §15.7).
   *
   * O redesenho é condicionado ao balcão aberto porque `render` reconstrói o DOM das três
   * janelas, e o inventário chega a cada item movido: redesenhar sempre apagaria o valor
   * digitado no campo do banco enquanto o jogador larga algo no chão, e derrubaria o foco
   * no meio da digitação.
   */
  setInventory(packet: InventoryPacket): void {
    this.inventory = packet;
    if (this.shop) this.render();
  }

  setBank(packet: BankPacket): void {
    this.bank = packet.open ? packet : null;
    this.render();
  }

  setTrade(packet: TradePacket): void {
    this.trade = packet.state === TradeState.Idle ? null : packet;
    this.render();
  }

  /** Fecha tudo. Chamado ao trocar de região e ao perder a conexão. */
  clear(): void {
    this.shop = null;
    this.bank = null;
    this.trade = null;
    this.render();
  }

  private render(): void {
    // Mesmo motivo do inventário: o `innerHTML = ''` mata a linha sob o cursor sem
    // `mouseleave`, e a dica dela ficaria órfã — e este painel redesenha a cada venda.
    hideItemTooltip();
    this.root.innerHTML = '';
    this.root.classList.toggle('visible', this.isOpen);

    if (this.trade) this.root.append(this.tradePanel(this.trade));
    if (this.shop) this.root.append(this.shopPanel(this.shop));
    if (this.bank) this.root.append(this.bankPanel(this.bank));
  }

  // --- Loja -----------------------------------------------------------------

  private shopPanel(packet: ShopPacket): HTMLElement {
    const panel = section('loja', packet.npcName, () => this.handlers.onCloseShop());

    // O conserto vem **acima** da vitrine porque é a única linha da janela que fala do que o
    // jogador já tem, e não do que está à venda: quem entra no armeiro com a cota rasgada
    // entra por causa dela. Abaixo de uma lista rolável ele ficaria fora da tela justamente
    // na loja com mais linhas — e um botão que exige rolar para ser descoberto é um botão
    // que ninguém aperta (PRD §15.7).
    //
    // `repairCost` zero cobre os dois "não" — nada gasto e NPC sem o ofício — e os dois dão
    // na mesma tela: nenhum botão. Desenhá-lo desabilitado só ensinaria o jogador a ignorar
    // aquele canto do painel.
    if (packet.repairCost > 0) {
      const repair = document.createElement('div');
      repair.className = 'shop-repair';
      repair.append(
        button(`consertar tudo — ${counted(packet.repairCost, 'moeda', 'moedas')}`, () =>
          this.handlers.onRepair(),
        ),
      );
      panel.append(repair);
    }

    const list = document.createElement('div');
    list.className = 'shop-list';

    for (const entry of packet.entries) {
      const type = itemType(entry.typeId);
      if (!type) continue;

      const row = document.createElement('div');
      row.className = 'shop-row';

      const name = document.createElement('span');
      name.className = 'shop-name';
      name.textContent = type.name;
      // A mesma dica do inventário, e não uma cópia: comprar é a decisão que mais precisa
      // dela — o preço está na linha, mas ataque, nível mínimo e peso estavam em lugar
      // nenhum. `count` 1 porque a vitrine oferece o tipo, não uma pilha.
      attachItemTooltip(name, type, 1);

      const actions = document.createElement('div');
      actions.className = 'shop-actions';

      // Uma quantidade por linha, e não um campo global: comprar uma espada e cem flechas
      // na mesma visita é o caso normal, e um campo compartilhado obrigaria a corrigi-lo
      // entre as duas compras.
      const count = document.createElement('input');
      count.type = 'number';
      count.min = '1';
      count.max = '1000';
      count.value = '1';
      count.className = 'shop-count';

      if (entry.buyPrice > 0) {
        actions.append(
          button(`comprar ${entry.buyPrice}`, () =>
            this.handlers.onBuy(entry.typeId, amountOf(count)),
          ),
        );
      }
      if (entry.sellPrice > 0) {
        actions.append(
          button(`vender ${entry.sellPrice}`, () =>
            this.handlers.onSell(entry.typeId, amountOf(count)),
          ),
        );
      }

      row.append(name, count, actions);
      list.append(row);
    }

    panel.append(list);

    const fallback = this.fallbackSection(packet);
    if (fallback) panel.append(fallback);

    return panel;
  }

  /**
   * "O balcão também compra": o que o jogador carrega e este NPC leva fora da vitrine
   * (PRD §15.7).
   *
   * O servidor já aceitava a venda; o que faltava era **dizer** que ela existe. Um preço de
   * fallback que só aparece para quem tenta vender é um preço que não existe: ninguém
   * descobre que o taverneiro paga pelo peixe da pescaria tentando vender peixe ao
   * taverneiro por acaso.
   *
   * O preço vem de `fallbackSellPrice`, e não de uma conta escrita aqui. Uma segunda cópia
   * da margem discordaria da primeira no dia em que `FALLBACK_SELL_RATE` mudasse, e o
   * sintoma seria a pior espécie: o botão anuncia 40 e o servidor paga 30.
   */
  private fallbackSection(packet: ShopPacket): HTMLElement | null {
    if (packet.specialty === 0 || !this.inventory) return null;

    const specialty = specialtyOf(packet.specialty);
    const showcase = new Set(packet.entries.map((entry) => entry.typeId));

    // Contêiner com algo dentro fica de fora: o pedido de venda viaja por tipo e quantidade,
    // e o servidor gasta a instância inteira — vender a mochila levaria junto o que está
    // dentro dela, que é o que o jogador **menos** queria vender. Esvaziá-la primeiro é um
    // gesto que ele entende; perder o loot num clique não é.
    const filled = new Set<number>();
    for (const entry of this.inventory.entries) {
      if (entry.parentId !== CONTAINER_EQUIPMENT) filled.add(entry.parentId);
    }

    // Soma por tipo, e não uma linha por pilha: três pilhas de peixe são "12× Peixe" para
    // quem está no balcão. Uma linha por pilha daria três botões que fazem a mesma coisa, e
    // cada um deles mentindo sobre o próprio teto — o servidor gasta de onde houver.
    //
    // O que está **vestido** não entra. O pedido só carrega o tipo, então o servidor pode
    // muito bem tirar a cota do corpo para atender uma venda que o jogador achou que era da
    // mochila. Quem quer vender o que está vestindo tira primeiro — e vê o item aparecer.
    const carried = new Map<number, number>();
    for (const entry of this.inventory.entries) {
      if (entry.parentId === CONTAINER_EQUIPMENT) continue;
      if (filled.has(entry.id)) continue;
      // Fora da vitrine, senão o mesmo item teria duas linhas com dois preços diferentes na
      // mesma janela — e a linha declarada é a que paga melhor.
      if (showcase.has(entry.typeId)) continue;
      if (fallbackSellPrice(entry.typeId, specialty) <= 0) continue;

      carried.set(entry.typeId, (carried.get(entry.typeId) ?? 0) + entry.count);
    }

    if (carried.size === 0) return null;

    const block = document.createElement('div');
    block.className = 'shop-fallback';

    const heading = document.createElement('h4');
    heading.className = 'shop-section';
    heading.textContent = 'O balcão também compra';
    block.append(heading);

    for (const [typeId, count] of carried) {
      const type = itemType(typeId);
      if (!type) continue;

      const price = fallbackSellPrice(typeId, specialty);

      const row = document.createElement('div');
      row.className = 'shop-row';

      const name = document.createElement('span');
      name.className = 'shop-name';
      name.textContent = `${count}× ${type.name}`;
      // Aqui o `count` real importa: a linha de peso da dica mostra o total da pilha, que
      // é o que se está prestes a tirar das costas.
      attachItemTooltip(name, type, count);

      // Campo de quantidade, como nas linhas de vitrine, e **não** um "vender tudo": esta
      // seção existe para esvaziar a mochila depois da caçada, mas ela lista comida, e um
      // botão que vende a pilha inteira venderia a reserva de pão no mesmo gesto. O valor
      // inicial é o total justamente porque esvaziar é o caso comum; o teto é o que se
      // carrega, para o cliente nunca pedir mais do que mostrou.
      //
      // Limitado ao mesmo teto por operação que o servidor aplica — os mesmos 1000 do campo
      // das linhas de vitrine: oferecer 1500 flechas num pedido que o servidor corta em 1000
      // seria prometer uma venda que sai pela metade sem explicação nenhuma.
      const ceiling = Math.min(count, 1000);

      const amount = document.createElement('input');
      amount.type = 'number';
      amount.min = '1';
      amount.max = String(ceiling);
      amount.value = String(ceiling);
      amount.className = 'shop-count';

      const actions = document.createElement('div');
      actions.className = 'shop-actions';
      actions.append(
        button(`vender ${price}`, () =>
          this.handlers.onSell(typeId, Math.min(amountOf(amount), ceiling)),
        ),
      );

      row.append(name, amount, actions);
      block.append(row);
    }

    const note = document.createElement('p');
    note.className = 'bank-note';
    // O desconto é a razão de a vitrine existir, e some se ninguém disser que ele está ali:
    // sem esta linha, o jogador que vende tudo no primeiro balcão nunca procura o segundo.
    note.textContent = 'Fora da vitrine o balcão paga menos. Vale procurar quem é do ramo.';
    block.append(note);

    return block;
  }

  // --- Banco ----------------------------------------------------------------

  private bankPanel(packet: BankPacket): HTMLElement {
    const panel = section('banco', 'Banco', () => this.handlers.onCloseBank());

    const balance = document.createElement('p');
    balance.className = 'bank-balance';
    // Mesma regra do servidor, e não uma cópia dela: esta linha e o "Você depositou 1
    // moeda." do `systems/commerce.ts` aparecem na tela juntas, uma logo abaixo da outra.
    balance.textContent = `Saldo: ${counted(Math.floor(packet.balance), 'moeda', 'moedas')}`;

    const amount = document.createElement('input');
    amount.type = 'number';
    amount.min = '1';
    amount.value = '100';
    amount.className = 'bank-amount';

    const actions = document.createElement('div');
    actions.className = 'bank-actions';
    actions.append(
      button('depositar', () => this.handlers.onDeposit(amountOf(amount))),
      button('sacar', () => this.handlers.onWithdraw(amountOf(amount))),
    );

    const note = document.createElement('p');
    note.className = 'bank-note';
    note.textContent = 'O que está aqui não se perde ao morrer.';

    panel.append(balance, amount, actions, note);
    return panel;
  }

  // --- Troca ----------------------------------------------------------------

  private tradePanel(packet: TradePacket): HTMLElement {
    const panel = section(
      'troca',
      packet.state === TradeState.Pending
        ? `Troca com ${packet.partnerName || 'alguém'}`
        : `Trocando com ${packet.partnerName || 'alguém'}`,
      () => this.handlers.onTradeCancel(),
    );

    if (packet.state === TradeState.Pending) {
      const invite = document.createElement('p');
      invite.className = 'trade-note';
      invite.textContent = 'Convite pendente. Aceitar abre a mesa dos dois lados.';

      panel.append(invite, button('aceitar convite', () => this.handlers.onTradeAccept()));
      return panel;
    }

    const columns = document.createElement('div');
    columns.className = 'trade-columns';
    columns.append(
      this.tradeColumn('Você oferece', packet.mine, packet.mineAccepted, true),
      this.tradeColumn(packet.partnerName, packet.theirs, packet.theirsAccepted, false),
    );

    const drop = document.createElement('div');
    drop.className = 'trade-drop';
    drop.textContent = 'arraste um item para cá';
    drop.addEventListener('dragover', (event) => {
      event.preventDefault();
      drop.classList.add('drop-target');
    });
    drop.addEventListener('dragleave', () => drop.classList.remove('drop-target'));
    drop.addEventListener('drop', (event) => {
      event.preventDefault();
      drop.classList.remove('drop-target');

      const raw = event.dataTransfer?.getData('application/x-hexaria-item');
      if (!raw) return;

      const source = JSON.parse(raw) as { container: number; slot: number };
      this.handlers.onTradeOffer(source.container, source.slot);
    });

    const hint = document.createElement('p');
    hint.className = 'trade-note';
    // A regra que a janela existe para tornar visível: qualquer mudança derruba as duas
    // confirmações, e é por isso que o que se confirma é sempre o que se leu.
    hint.textContent = `Arraste itens do inventário. Qualquer mudança cancela as confirmações. Máximo ${MAX_TRADE_ITEMS} itens.`;

    panel.append(
      columns,
      drop,
      hint,
      button(packet.mineAccepted ? 'aguardando o outro…' : 'confirmar', () =>
        this.handlers.onTradeAccept(),
      ),
    );

    return panel;
  }

  private tradeColumn(
    title: string,
    items: TradePacket['mine'],
    accepted: boolean,
    mine: boolean,
  ): HTMLElement {
    const column = document.createElement('div');
    column.className = 'trade-column';

    const heading = document.createElement('h4');
    heading.className = 'trade-heading';
    heading.textContent = accepted ? `${title} ✓` : title;
    column.append(heading);

    if (items.length === 0) {
      const empty = document.createElement('p');
      empty.className = 'trade-empty';
      empty.textContent = '(vazio)';
      column.append(empty);
      return column;
    }

    items.forEach((item, index) => {
      const row = document.createElement('div');
      row.className = 'trade-item';

      const type = itemType(item.typeId);
      row.textContent = `${item.count > 1 ? `${item.count}× ` : ''}${type?.name ?? '?'}`;
      // Na mesa é onde a dica mais vale: o que se confirma numa troca é o que se leu, e um
      // nome sozinho não diz se a "Espada de aço" oferecida é a rara ou a comum.
      if (type) attachItemTooltip(row, type, item.count);

      // Só a própria oferta é retirável: mexer na do outro seria o oposto do que a janela
      // promete.
      if (mine) {
        row.append(button('retirar', () => this.handlers.onTradeWithdraw(index)));
      }

      column.append(row);
    });

    return column;
  }
}

/**
 * A caixa de uma das três janelas, com a barra que fecha **e** a que recolhe.
 *
 * Os dois gestos convivem na mesma linha porque respondem a coisas diferentes: "fechar" manda
 * pacote e encerra a conversa com o mercador; "recolher" só tira a vitrine da frente. Numa
 * troca entre jogadores a diferença é a que importa — cancelar desfaz o acordo, e quem só
 * precisa enxergar a mochila para escolher o que oferecer quer o segundo gesto, não o
 * primeiro. É por isso que o `fechar` entra como ação do cabeçalho (`stopPropagation`, ver
 * `panel.ts`) e não como mais um botão que o clique de recolher engoliria.
 *
 * `id` separa as três no disco: quem recolhe o banco não recolhe a loja junto.
 */
function section(id: PanelId, title: string, onClose: () => void): HTMLElement {
  const panel = document.createElement('section');
  panel.className = 'commerce-panel';

  const header = collapsiblePanel(panel, id, title, {
    actions: [button('fechar', onClose, 'ghost')],
  });

  panel.append(header.root);
  return panel;
}

function button(label: string, onClick: () => void, extra = ''): HTMLButtonElement {
  const element = document.createElement('button');
  element.type = 'button';
  element.className = `commerce-button${extra ? ` ${extra}` : ''}`;
  element.textContent = label;
  element.addEventListener('click', onClick);
  return element;
}

/** Todas as categorias do catálogo, para percorrer a máscara sem escrevê-las à mão. */
const ITEM_KINDS: readonly ItemKind[] = Object.values(ItemKind);

/**
 * A máscara do pacote de volta para a lista de categorias que `fallbackSellPrice` espera.
 *
 * Tradução, e não uma segunda regra de preço: quem decide se o item é vendável continua
 * sendo a função do protocolo — com a raridade, o `value` e a especialidade que o servidor
 * usa. Aqui só se desfaz o empacotamento que o `u16` do pacote exigiu (PRD §15.7).
 */
function specialtyOf(mask: number): ShopSpecialty {
  return ITEM_KINDS.filter((kind) => specialtyHas(mask, kind));
}

/** Quantidade digitada, saneada. O servidor revalida — isto é só para não mandar lixo. */
function amountOf(input: HTMLInputElement): number {
  const parsed = Number.parseInt(input.value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}
